import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, IconButton, Divider, Theme, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { CardPaper } from 'shared/ui/card-paper';
import { SxProps } from '@mui/system';
import { HeaderText } from 'shared/ui/header-text';

type Props = {
  title: ReactNode,
  actions?: ReactNode,
  subHeader?: ReactNode,
  sx?: SxProps<Theme>;
  fallbackTo?: string;
  preferFallbackTo?: boolean;
};

export const PageHeader = ({ title, actions, subHeader, sx, fallbackTo = '/', preferFallbackTo = false }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(from);
      return;
    }

    if (preferFallbackTo) {
      navigate(fallbackTo, { replace: true });
      return;
    }

    const historyIdx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1);
      return;
    }

    navigate(fallbackTo, { replace: true });
  };

  return (
    <Box sx={sx || { mb: 2, mt: 1 }}>
      <CardPaper>
        <Stack direction="row" alignItems="center">
          <Box>
            <IconButton color='primary' onClick={handleBack}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>

          <Box>
            {title}
          </Box>

          {actions &&
            <Box sx={{ ml: 'auto', mr: 1 }}>
              {actions}
            </Box>
          }
        </Stack>

        {subHeader && (
          <>
            <Divider sx={{ my: 1 }} />
            {subHeader}
          </>
        )}
      </CardPaper>
    </Box>);
};

PageHeader.Text = HeaderText;
